import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _keys from 'lodash/keys';
import _startCase from 'lodash/startCase';

import { FuzzySearch } from 'components/modules/foundation/services/search';
import { generateAlphabeticUuid } from 'libs/uuid';

import { ACTIVE_STATE, ANY_UNIT, INACTIVE_STATE } from '../../constants';
import { formatStyleRuleName } from '../../services/format';

export function filterStyleRuleConfigurationsFolder(styleRuleConfigurations, selectedFolder) {
  if (_isEmpty(selectedFolder)) {
    return styleRuleConfigurations;
  }

  return styleRuleConfigurations.filter((config) => {
    if (_isEmpty(selectedFolder)) {
      return true;
    }

    return selectedFolder.every((folder, index) => config.hierarchy[index] === folder);
  });
}

export function filterStyleRuleConfigurationsByLocalSearchQuery(
  styleRuleConfigurations,
  localSearchQuery,
) {
  const styleRule = styleRuleConfigurations.find(
    ({ name }) => formatStyleRuleName(name) === formatStyleRuleName(localSearchQuery),
  );

  if (!_isEmpty(styleRule)) {
    return [styleRule];
  }

  const options = styleRuleConfigurations.map(({ name, ...rest }) => ({
    displayName: formatStyleRuleName(name),
    name,
    ...rest,
  }));

  const fuzzyInstance = new FuzzySearch(options, ['displayName']);
  return fuzzyInstance.search(formatStyleRuleName(localSearchQuery));
}

export function filterStyleRuleConfigurationsByState(
  styleRuleConfigurations,
  selectiveStates,
  styleRules,
) {
  const activeStyleRuleNames = styleRules.map(({ name }) => `@${name}`);

  return styleRuleConfigurations.filter(({ name }) => {
    const isActive = activeStyleRuleNames.includes(name);
    const activeFilterEnabled = selectiveStates.includes(ACTIVE_STATE);
    const inactiveFilterEnabled = selectiveStates.includes(INACTIVE_STATE);

    return (activeFilterEnabled && isActive) || (inactiveFilterEnabled && !isActive);
  });
}

export function filterStyleRuleConfigurationsByUnit(styleRuleConfigurations, selectedUnits) {
  return styleRuleConfigurations.filter(({ unit }) => selectedUnits.includes(unit));
}

export function createStyleRuleConfigurationsFolderItems(styleRuleConfigurations) {
  const folderStructure = {};

  styleRuleConfigurations.forEach(({ hierarchy }) => {
    let currentLevel = folderStructure;
    hierarchy.forEach((folder, index) => {
      const currentFolder = currentLevel[folder];
      const isLastFolder = index === hierarchy.length - 1;
      if (!currentFolder) {
        currentLevel[folder] = isLastFolder ? null : {};
      }
      currentLevel = currentLevel[folder];
    });
  });

  function sortFoldersAndFiles(foldersOrFiles) {
    return foldersOrFiles.sort((a, b) => {
      if (a.items.length === 0 && b.items.length > 0) return 1;
      if (a.items.length > 0 && b.items.length === 0) return -1;
      return a.name.localeCompare(b.name);
    });
  }

  function convertToItems(folder, parentFolderKey = '') {
    return _keys(folder).map((folderKey) => {
      const fullFolderKey = parentFolderKey ? `${parentFolderKey}.${folderKey}` : folderKey;

      return {
        name: _startCase(folderKey),
        id: fullFolderKey,
        items: _isNil(folder[folderKey])
          ? []
          : sortFoldersAndFiles(convertToItems(folder[folderKey], fullFolderKey)),
      };
    });
  }

  return sortFoldersAndFiles(convertToItems(folderStructure));
}

export function isStyleRuleVariable(styleRuleName) {
  if (_isEmpty(styleRuleName)) {
    return false;
  }

  return styleRuleName.startsWith('@') && !styleRuleName.includes(' ');
}

export function ensureStyleRuleName(styleRuleName) {
  if (_isEmpty(styleRuleName)) {
    return '';
  }

  return isStyleRuleVariable(styleRuleName) ? styleRuleName : `@${styleRuleName}`;
}

export function getStyleRuleByName(styleRuleName, styleRules) {
  return styleRules.find(
    ({ name }) => ensureStyleRuleName(name) === ensureStyleRuleName(styleRuleName),
  );
}

export function getStyleRuleInheritanceFromConfigurations(
  styleRuleValue,
  styleRules,
  styleRuleConfigurationsMap,
) {
  if (_isEmpty(styleRuleValue)) {
    return [];
  }

  if (!isStyleRuleVariable(styleRuleValue)) {
    return [styleRuleValue];
  }

  const inheritanceList = [];
  const visitedRules = new Set();
  let currentStyleRuleName = ensureStyleRuleName(styleRuleValue);

  while (
    styleRuleConfigurationsMap[currentStyleRuleName] &&
    !visitedRules.has(currentStyleRuleName)
  ) {
    inheritanceList.push(currentStyleRuleName);
    visitedRules.add(currentStyleRuleName);
    const { defaultValue } = styleRuleConfigurationsMap[currentStyleRuleName];
    const currentStyleRule = getStyleRuleByName(currentStyleRuleName, styleRules);
    const currentValue = currentStyleRule ? currentStyleRule.value : defaultValue;

    if (!isStyleRuleVariable(currentValue)) {
      inheritanceList.push(currentValue);

      return inheritanceList;
    }

    currentStyleRuleName = ensureStyleRuleName(currentValue);

    // If we've encountered this rule before, we have a circular reference
    if (visitedRules.has(currentStyleRuleName)) {
      inheritanceList.push(currentStyleRuleName);
      return inheritanceList;
    }
  }

  return inheritanceList;
}

export function fillCustomStyleRulesWithConfigurations(styleRuleConfigurations, styleRules) {
  const newConfigurations = [];

  styleRules.forEach(({ name }) => {
    if (
      styleRuleConfigurations.find(
        ({ name: configurationName }) =>
          formatStyleRuleName(name) === formatStyleRuleName(configurationName),
      )
    ) {
      return;
    }

    newConfigurations.push({
      id: generateAlphabeticUuid(),
      name: ensureStyleRuleName(name),
      defaultValue: undefined,
      description: '',
      hierarchy: [],
      unit: ANY_UNIT,
      custom: true,
    });
  });

  return [...styleRuleConfigurations, ...newConfigurations];
}
